import React, { useId, useRef } from 'react';
import PropTypes from 'prop-types';
import IconOnlyButton from 'components/Buttons/IconOnlyButton';
import Portal from 'utils/Portal';
import useCloseOnOutsideClick from 'utils/hooks/useCloseOnOutsideClick';
import useTrapFocusInsideModal from 'utils/hooks/useTrapFocusInsideModal';

/**
 * Modal component.
 *
 * @param {string} sPortalId - The id of the root element to render the modal in.
 * @param {node} children - The content of the modal. ie. action buttons, text, etc.
 * @param {function} fnSetShowModal - The function to set the state of the modal.
 * @param {string} sFlavor - The flavor of the modal. One of 'sign-in', 'standard', 'alert', or 'message'.
 * @param {string} sModalTitle - The title of the modal.
 * @param {string} sModalSubtitle - The subtitle of the modal.
 * @param {string or object} sModalDetail - The sub subtitle of the modal.
 * @param {function} fnOnBack - Optional callback for a back button in the header.
 *
 * @returns {node} - The modal.
 *
 */

const Modal = ({
  sPortalId,
  children,
  fnSetShowModal,
  sFlavor,
  sModalTitle,
  sModalSubtitle,
  sModalDetail, // ModalSubSubtitle
  sCypressId,
  fnOnBack,
}) => {
  const oModalRef = useRef();
  const sTitleId = useId();
  useCloseOnOutsideClick(oModalRef, fnSetShowModal);
  // Modal content can mount/unmount focusable elements at runtime (e.g.
  // advanced search's back button appears when results load). Re-query on
  // each Tab so those are reachable.
  useTrapFocusInsideModal(oModalRef, { bQueryDynamically: true });
  return (
    <Portal sRootElementId={sPortalId}>
      <div className='modal__overlay'>
        <dialog
          className={`modal modal--${sFlavor}`}
          ref={oModalRef}
          data-cy={sCypressId}
          aria-modal='true'
          aria-labelledby={sModalTitle ? sTitleId : undefined}
        >
          {sFlavor !== 'standard' && (
            <IconOnlyButton
              sIcon='close'
              fnHandleClick={() => fnSetShowModal(false)}
              sAriaLabel='Close'
              className='modal__closeButton'
            />
          )}
          {sFlavor !== 'message' && (
            <div className={`modal__header modal__header--${sFlavor}`}>
              {sFlavor === 'standard' && (
                <>
                  {fnOnBack ? (
                    <IconOnlyButton
                      sIcon='arrowTurnLeft'
                      fnHandleClick={fnOnBack}
                      sAriaLabel='Back'
                      className='modal__backButton'
                      sFlavor='tertiary'
                    />
                  ) : (
                    <span />
                  )}
                </>
              )}
              <p id={sTitleId} className='modal__title t-paragraph--medium'>
                {sModalTitle}
              </p>
              {sFlavor === 'standard' && (
                <IconOnlyButton
                  sIcon='close'
                  fnHandleClick={() => fnSetShowModal(false)}
                  sAriaLabel='Close'
                  className='modal__headerCloseButton'
                  sFlavor='tertiary'
                />
              )}
            </div>
          )}
          <div className={`modal__content modal__content--${sFlavor}`}>
            {sModalSubtitle && (
              <p className='modal__subtitle t-paragraph--large'>
                {sModalSubtitle}
              </p>
            )}
            {sModalDetail && (
              <div className='modal__detail t-paragraph'>{sModalDetail}</div>
            )}
            {(sModalSubtitle || sModalDetail) && sFlavor !== 'message' && (
              <hr className='modal__theLine' />
            )}
            <div className='modal__children'>{children}</div>
          </div>
        </dialog>
      </div>
    </Portal>
  );
};

// @todo: Convert these to TypeScript. See the declaration file Modal.d.ts.
Modal.propTypes = {
  sPortalId: PropTypes.string,
  children: PropTypes.node,
  fnSetShowModal: PropTypes.func.isRequired,
  sFlavor: PropTypes.oneOf(['sign-in', 'standard', 'alert', 'message']),
  sModalTitle: PropTypes.string,
  sModalSubtitle: PropTypes.string,
  sModalDetail: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  fnOnBack: PropTypes.func,
};

Modal.defaultProps = {
  sPortalId: 'app',
  children: null,
  sFlavor: 'message',
  sModalTitle: '',
  sModalSubtitle: '',
  sModalDetail: '',
  fnOnBack: null,
};

export default Modal;
